import { useEffect, useLayoutEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Link } from 'react-router';
import { Dialog as D, Select as S } from 'radix-ui';
import { ArrowDown, ArrowUp, Check, ChevronLeft, ChevronRight, ChevronDown, Search, X } from 'lucide-react';
import { cn, errorMessage, storage } from '@/lib/utils';
import { Button } from './button';
import { Input } from './form';
import { Skeleton } from './misc';
export function Page({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <h1>{title}</h1>
          {description && <p>{description}</p>}
        </div>
        {actions && <div className="page-actions">{actions}</div>}
      </div>
      {children}
    </div>
  );
}
export function Panel({
  title,
  description,
  action,
  children,
  className,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('panel', className)}>
      {title && (
        <div className="panel-heading">
          <div>
            <h2>{title}</h2>
            {description && <p>{description}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}
export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="toolbar">{children}</div>;
}
export function SearchField({
  value,
  onChange,
  placeholder = 'Search',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="search-field">
      <Search size={16} />
      <Input
        aria-label={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {value && (
        <button aria-label="Clear search" onClick={() => onChange('')}>
          <X size={14} />
        </button>
      )}
    </div>
  );
}
export { Chips } from './chips';
export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="form-field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}
export function Select({
  value,
  onChange,
  options,
  label,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: {
    value: string;
    label: string;
  }[];
  label: string;
  disabled?: boolean;
}) {
  return (
    <S.Root
      disabled={disabled}
      value={value || '__all__'}
      onValueChange={(v) => onChange(v === '__all__' ? '' : v)}
    >
      <S.Trigger className="select-control custom-select-trigger" aria-label={label}>
        <S.Value />
        <S.Icon>
          <ChevronDown size={14} />
        </S.Icon>
      </S.Trigger>
      <S.Portal>
        <S.Content position="popper" sideOffset={5} collisionPadding={8} className="custom-select-menu">
          <S.Viewport>
            {options.map((o) => (
              <S.Item key={o.value} value={o.value || '__all__'} className="custom-select-option">
                <S.ItemText>{o.label}</S.ItemText>
                <S.ItemIndicator>
                  <Check size={14} />
                </S.ItemIndicator>
              </S.Item>
            ))}
          </S.Viewport>
        </S.Content>
      </S.Portal>
    </S.Root>
  );
}
export function Notice({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'warning' | 'success';
}) {
  return <div className={cn('notice', `notice-${tone}`)}>{children}</div>;
}
export function Stat({
  label,
  value,
  detail,
  icon,
  to,
  onClick,
}: {
  label: string;
  value: ReactNode;
  detail?: string;
  icon?: ReactNode;
  to?: string;
  onClick?: () => void;
}) {
  const content = (
    <>
      <div className="stat-label">
        {label}
        {icon}
      </div>
      <div className="stat-value">{value}</div>
      {detail && <p>{detail}</p>}
    </>
  );
  if (to)
    return (
      <Link to={to} className="stat stat-interactive">
        {content}
      </Link>
    );
  if (onClick)
    return (
      <button type="button" onClick={onClick} className="stat stat-interactive">
        {content}
      </button>
    );
  return <div className="stat">{content}</div>;
}
export function Stats({ children }: { children: ReactNode }) {
  return <div className="stats">{children}</div>;
}
export function Status({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger';
}) {
  return (
    <span className={cn('status', `status-${tone}`)}>
      <i />
      {children}
    </span>
  );
}
export function Tag({ children }: { children: ReactNode }) {
  return <span className="tag">{children}</span>;
}
export function QueryState({
  pending,
  updating = false,
  loading,
  error,
  empty,
  onRetry,
  children,
  emptyTitle = 'Nothing here yet',
  emptyDescription = 'Your data will appear here as it syncs.',
  emptyAction,
}: {
  pending: boolean;
  updating?: boolean;
  loading?: ReactNode;
  error?: unknown;
  empty?: boolean;
  onRetry?: () => void;
  children: ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
}) {
  const emptyContent = (
    <div className="empty">
      <div className="empty-symbol">
        <Check size={20} />
      </div>
      <h3>{emptyTitle}</h3>
      <p>{emptyDescription}</p>
      {emptyAction}
    </div>
  );
  if (error)
    return (
      <div className="empty" role="alert">
        <h3>Couldn’t load this</h3>
        <p>{errorMessage(error)}</p>
        {onRetry && (
          <Button variant="secondary" onClick={onRetry}>
            Try again
          </Button>
        )}
      </div>
    );
  if (pending)
    return (
      loading ?? (
        <div className="loading-rows" role="status" aria-label="Loading" aria-busy>
          <span className="sr-only">Loading results…</span>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )
    );
  if (updating)
    return (
      <div className="query-updating" aria-busy="true">
        <div className="query-stale" inert aria-hidden="true">
          {empty ? emptyContent : children}
        </div>
        <div className="query-loading-overlay" role="status" aria-label="Updating results">
          {loading ?? (
            <div className="loading-rows">
              <span className="sr-only">Updating results…</span>
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  if (empty) return emptyContent;
  return <>{children}</>;
}

/** Match table geometry while its first page loads; later fetches keep the existing footprint. */
export function TableLoading({
  columns = 5,
  rows = 10,
  width,
}: {
  columns?: number;
  rows?: number;
  width?: number;
}) {
  return (
    <div className="table-loading" role="status" aria-label="Loading results" aria-busy="true">
      <span className="sr-only">Loading results…</span>
      <Table width={width}>
        <thead aria-hidden="true">
          <tr>
            {Array.from({ length: columns }, (_, i) => (
              <th key={i}>
                <Skeleton className="h-3 w-16 max-w-full" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody aria-hidden="true">
          {Array.from({ length: Math.max(1, rows) }, (_, row) => (
            <tr key={row}>
              {Array.from({ length: columns }, (_, i) => (
                <td key={i}>
                  <Skeleton className="h-4 w-24 max-w-full" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
export function Table({ children, width }: { children: ReactNode; width?: number }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const hintId = useId();
  const [overflows, setOverflows] = useState(false);
  const [hintDismissed, setHintDismissed] = useState(() =>
    storage.get('cs_table_scroll_hint_dismissed', false),
  );
  useLayoutEffect(() => {
    const region = scrollRef.current;
    if (!region) return;
    const measure = () => setOverflows(region.scrollWidth > region.clientWidth + 1);
    const observer = new ResizeObserver(measure);
    observer.observe(region);
    if (region.firstElementChild) observer.observe(region.firstElementChild);
    measure();
    return () => observer.disconnect();
  }, []);
  return (
    <div className="table-container">
      {overflows && !hintDismissed && (
        <p id={hintId} className="table-scroll-hint">
          <span className="flex-1">Scroll this table sideways to see more columns.</span>
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Dismiss table scroll hint"
            title="Dismiss hint"
            onClick={() => {
              storage.set('cs_table_scroll_hint_dismissed', true);
              setHintDismissed(true);
            }}
          >
            <X size={14} />
          </Button>
        </p>
      )}
      <div
        ref={scrollRef}
        className="table-scroll"
        tabIndex={0}
        role="region"
        aria-label="Scrollable data table"
        aria-describedby={overflows && !hintDismissed ? hintId : undefined}
      >
        <table
          className={`data-table${width ? ' customizable-table' : ''}`}
          style={width ? { width } : undefined}
        >
          {children}
        </table>
      </div>
    </div>
  );
}
export function SortHeading({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active?: boolean;
  dir?: string;
  onClick: () => void;
}) {
  return (
    <th aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button onClick={onClick}>
        {label}
        {active ? (
          dir === 'asc' ? (
            <ArrowUp size={12} />
          ) : (
            <ArrowDown size={12} />
          )
        ) : (
          <span className="sort-hint">↕</span>
        )}
      </button>
    </th>
  );
}
export function Pager({
  page,
  size,
  total,
  onPage,
  onSize,
  pending = false,
}: {
  page: number;
  size: number;
  total: number;
  onPage: (v: number) => void;
  onSize?: (v: number) => void;
  pending?: boolean;
}) {
  const max = Math.max(1, Math.ceil(total / size));
  return (
    <div className="pager" aria-busy={pending}>
      <span>
        {pending
          ? 'Loading results…'
          : `${total ? `${(page - 1) * size + 1}–${Math.min(page * size, total)}` : '0'} of ${total.toLocaleString('en-IN')}`}
      </span>
      <div>
        {onSize && (
          <Select
            value={String(size)}
            label="Rows per page"
            onChange={(v) => onSize(Number(v))}
            disabled={pending}
            options={[25, 50, 100].map((n) => ({
              value: String(n),
              label: `${n} rows`,
            }))}
          />
        )}
        <Button
          size="icon-sm"
          variant="ghost"
          disabled={pending || page <= 1}
          disabledReason={pending ? 'Wait for this page to load.' : 'You are on the first page.'}
          aria-label="Previous page"
          onClick={() => onPage(page - 1)}
        >
          <ChevronLeft size={16} />
        </Button>
        <span>
          {page} / {max}
        </span>
        <Button
          size="icon-sm"
          variant="ghost"
          disabled={pending || page >= max}
          disabledReason={pending ? 'Wait for this page to load.' : 'You are on the last page.'}
          aria-label="Next page"
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight size={16} />
        </Button>
      </div>
    </div>
  );
}
function useOverlayFocus(initialFocusRef?: RefObject<HTMLElement | null>) {
  const origin = useRef<{ element: HTMLElement | null; path: string } | null>(null);
  return {
    onOpenAutoFocus: (event: Event) => {
      origin.current = {
        element: document.activeElement as HTMLElement | null,
        path: window.location.pathname,
      };
      if (initialFocusRef?.current) {
        event.preventDefault();
        initialFocusRef.current.focus({ preventScroll: true });
      }
    },
    onCloseAutoFocus: (event: Event) => {
      event.preventDefault();
      if (origin.current?.path === window.location.pathname && origin.current.element?.isConnected)
        origin.current.element.focus({ preventScroll: true });
    },
  };
}
// History entries of overlays that just closed, by overlay level, with the address they were on.
const closedEntries = new Map<number, string>();
let dropTimer: number | null = null;
const overlayLevel = () => {
  const state = window.history.state as Record<string, unknown> | null;
  return typeof state?.overlay === 'number' ? state.overlay : 0;
};
const currentAddress = () => window.location.pathname + window.location.search;

/** Drops, in one step, the entries of overlays that closed together (a confirm and its dialog). */
function dropClosedEntries() {
  dropTimer = null;
  let level = overlayLevel();
  let count = 0;
  while (level > 0 && closedEntries.get(level) === currentAddress()) {
    count++;
    level--;
  }
  closedEntries.clear();
  if (count) window.history.go(-count);
}

/**
 * While an overlay is open it owns one history entry on the same URL, so the
 * phone's Back button (or the browser's) closes the overlay instead of leaving
 * the page and losing what was typed. Nested overlays close one at a time.
 */
function useBackCloses(open: boolean, onClose: () => void, enabled = true) {
  const latest = useRef(onClose);
  latest.current = onClose;
  const closedLevel = useRef<number | null>(null);
  useEffect(() => {
    if (!open || !enabled) return;
    let level: number;
    // Set up again straight away (React's development double effects): keep the entry.
    if (closedLevel.current !== null && closedEntries.delete(closedLevel.current)) {
      level = closedLevel.current;
    } else {
      const current = window.history.state;
      const base = current && typeof current === 'object' ? (current as Record<string, unknown>) : {};
      level = (typeof base.overlay === 'number' ? base.overlay : 0) + 1;
      window.history.pushState({ ...base, overlay: level }, '');
    }
    closedLevel.current = null;
    const path = currentAddress();
    let popped = false;
    const onPop = () => {
      if (popped || overlayLevel() >= level) return;
      popped = true;
      latest.current();
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // Closed with X, Escape or an action: drop the entry this overlay added, a tick later so
      // overlays closing together leave in one step.
      if (popped || overlayLevel() < level || currentAddress() !== path) return;
      closedEntries.set(level, path);
      closedLevel.current = level;
      dropTimer ??= window.setTimeout(dropClosedEntries, 0);
    };
  }, [open, enabled]);
}
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  wide = false,
  className,
  initialFocusRef,
  busy = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  className?: string;
  initialFocusRef?: RefObject<HTMLElement | null>;
  busy?: boolean;
}) {
  const focus = useOverlayFocus(initialFocusRef);
  useBackCloses(open, () => {
    if (!busy) onClose();
  });
  return (
    <D.Root open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <D.Portal>
        <D.Overlay className="dialog-scrim" />
        <D.Content
          {...focus}
          onEscapeKeyDown={(event) => {
            if (busy) event.preventDefault();
          }}
          className={cn('dialog-frame', wide && 'dialog-wide', className)}
        >
          <div className="dialog-heading">
            <div>
              <D.Title>{title}</D.Title>
              <D.Description className={!description ? 'sr-only' : ''}>{description || title}</D.Description>
            </div>
            <D.Close asChild>
              <Button
                aria-label="Close"
                disabled={busy}
                variant="ghost"
                size="icon-sm"
                disabledReason="Please wait while the current action finishes."
              >
                <X size={18} />
              </Button>
            </D.Close>
          </div>
          <div className="dialog-body">{children}</div>
          {footer && <div className="dialog-footer">{footer}</div>}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
export function Drawer({
  open,
  onClose,
  title,
  heading,
  children,
  footer,
  actions,
  className,
  backCloses = true,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  heading?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  actions?: ReactNode;
  className?: string;
  /** Off when the caller keeps its own history entry (the contact panel). */
  backCloses?: boolean;
}) {
  const focus = useOverlayFocus();
  useBackCloses(open, onClose, backCloses);
  return (
    <D.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <D.Portal>
        <D.Overlay className="dialog-scrim" />
        <D.Content {...focus} className={cn('drawer-frame', className)}>
          <div className="dialog-heading">
            {heading ? (
              <div className="drawer-heading-content">
                {heading}
                <D.Title className="sr-only">{title}</D.Title>
              </div>
            ) : (
              <D.Title>{title}</D.Title>
            )}
            <D.Description className="sr-only">{title}</D.Description>
            <div className="flex items-center gap-1">
              {actions}
              <D.Close asChild>
                <Button aria-label="Close panel" variant="ghost" size="icon-sm">
                  <X size={18} />
                </Button>
              </D.Close>
            </div>
          </div>
          <div className="drawer-body">{children}</div>
          {footer && <div className="dialog-footer">{footer}</div>}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
export function Disclosure({ title, children }: { title: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="disclosure">
      <button aria-expanded={open} onClick={() => setOpen(!open)}>
        {title}
        <span>{open ? '−' : '+'}</span>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}
